import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Checkbox, ErrorBanner } from '@/components/form';
import { FileDrop, StatusChip } from '@/components/migration/file-drop';
import { GetFromQuickBooks } from '@/components/migration/quickbooks';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { downloadCsv } from '@/lib/imports';
import {
  fmtAmount,
  isZero,
  readUpload,
  useMigration,
  type MigrationView,
  type OpenItemsResult,
  type Side,
  type TieOut,
  type Upload,
} from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/migration/open-items/$side')({
  beforeLoad: ({ params }) => {
    if (params.side !== 'receivable' && params.side !== 'payable') throw notFound();
  },
  component: OpenItemsPage,
});

/** Screen G9 (document 06): open invoices or unpaid bills at the conversion date. */
function OpenItemsPage() {
  const side = Route.useParams().side as Side;
  const orgId = useOrgId();
  const me = useMe();
  const m = useMigration();
  const t = useT();
  return (
    <>
      <PageHeader
        title={t(`settingsPages.migration.openItems.${side}.title`)}
        description={t(`settingsPages.migration.openItems.${side}.description`)}
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/migration">{t('settingsPages.migration.moveFromQuickBooks')}</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState title={t('settingsPages.migration.openItems.staffOnly')} />
      ) : m.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : m.isError ? (
        <EmptyState title={t('settingsPages.migration.couldNotLoad')} hint={m.error.message} />
      ) : !m.data.conversionDate ? (
        <EmptyState
          title={t('settingsPages.migration.setDateFirst')}
          action={
            <Button asChild>
              <Link to="/settings/migration">
                {t('settingsPages.migration.moveFromQuickBooks')}
              </Link>
            </Button>
          }
        />
      ) : m.data.status === 'finished' ? (
        <EmptyState
          title={t('settingsPages.migration.finished')}
          hint={t('settingsPages.migration.openItems.finishedHint')}
        />
      ) : (
        <Wizard key={`${orgId}-${side}`} side={side} view={m.data} />
      )}
    </>
  );
}

function TieStrip({ side, tie }: { side: Side; tie: TieOut }) {
  const off = !isZero(tie.difference);
  const t = useT();
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border px-4 py-3 text-sm',
        off ? 'border-warning/40 bg-warning/10' : 'border-success/30 bg-success/10',
      )}
      role="status"
      data-testid="tie-out"
    >
      <span>
        {t(`settingsPages.migration.openItems.${side}.inTrialBalance`)}{' '}
        <strong className="tnum">{fmtAmount(tie.expected)}</strong>
      </span>
      <span>
        {t('settingsPages.migration.openItems.openItems')}{' '}
        <strong className="tnum">{fmtAmount(tie.imported)}</strong>
      </span>
      <span>
        {t('settingsPages.migration.openItems.difference')}{' '}
        <strong className="tnum">{fmtAmount(tie.difference)}</strong>
      </span>
      <StatusChip tone={off ? 'warning' : 'success'}>
        {off
          ? t('settingsPages.migration.openItems.doesNotTie')
          : t('settingsPages.migration.openItems.ties')}
      </StatusChip>
    </div>
  );
}

function Wizard({ side, view }: { side: Side; view: MigrationView }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const t = useT();
  const who = t(`settingsPages.migration.openItems.${side}.who`);
  const [file, setFile] = useState<Upload | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [onlyErrors, setOnlyErrors] = useState(false);

  const preview = useMutation({
    mutationFn: async (f: File) => {
      const u = await readUpload(f);
      setFile(u);
      return api<OpenItemsResult>(`/orgs/${orgId}/migration/open-items/${side}/preview`, {
        method: 'POST',
        body: JSON.stringify({ file: u }),
      });
    },
    onMutate: () => setFileError(null),
    onError: (e) => setFileError((e as Error).message),
  });
  const run = useMutation({
    mutationFn: () =>
      api<OpenItemsResult>(`/orgs/${orgId}/migration/open-items/${side}`, {
        method: 'POST',
        body: JSON.stringify({ file }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['migration', orgId] });
      await qc.invalidateQueries({ queryKey: ['sales', orgId] });
      await qc.invalidateQueries({ queryKey: ['purchases', orgId] });
    },
  });
  const result = run.data ?? preview.data;
  const done = Boolean(run.data);
  const rows = result
    ? onlyErrors
      ? result.rows.filter((r) => r.status === 'error')
      : result.rows
    : [];

  const reset = () => {
    setFile(null);
    preview.reset();
    run.reset();
  };

  if (!result)
    return (
      <div className="flex max-w-6xl flex-col gap-4">
        <TieStrip side={side} tie={view.tieOut[side]} />
        <ErrorBanner>{fileError}</ErrorBanner>
        <FileDrop
          title={t(`settingsPages.migration.openItems.${side}.dropTitle`)}
          hint={
            <Trans
              t={t}
              i18nKey={`settingsPages.migration.openItems.${side}.dropHint`}
              values={{ date: formatDate(view.openingDate!) }}
              components={{ b: <strong /> }}
            />
          }
          busy={preview.isPending}
          onFile={(f) => preview.mutate(f)}
        />
        <GetFromQuickBooks
          kind={side === 'receivable' ? 'open-receivables' : 'open-payables'}
          onFile={(f) => preview.mutate(f)}
        />
        {!view.trialBalance ? (
          <p className="text-sm text-muted-foreground">
            {t('settingsPages.migration.openItems.noTrialBalance')}{' '}
            <Link
              to="/settings/migration/opening-balances"
              className="text-primary hover:underline"
            >
              {t('settingsPages.migration.openItems.importOpeningBalances')}
            </Link>
          </p>
        ) : null}
      </div>
    );

  return (
    <div className="flex max-w-6xl flex-col gap-4">
      <TieStrip side={side} tie={result.tieOut} />
      <ErrorBanner>{run.error?.message}</ErrorBanner>
      {result.issues.length ? (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          {result.issues
            .map((i) =>
              i.row
                ? t('settingsPages.migration.rowIssue', { row: i.row, message: i.message })
                : i.message,
            )
            .join(' · ')}
        </div>
      ) : null}
      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          done ? 'border-primary/30 bg-primary/5' : 'bg-card',
        )}
        role="status"
      >
        <span className="font-medium">
          {t(
            done
              ? 'settingsPages.migration.openItems.importedSummary'
              : 'settingsPages.migration.openItems.readySummary',
            {
              ok: result.ok,
              total: result.rows.length,
              amount: fmtAmount(result.total),
            },
          )}
        </span>{' '}
        {result.errors
          ? t(
              done
                ? 'settingsPages.migration.openItems.errorsSkipped'
                : 'settingsPages.migration.openItems.errorsWillBeSkipped',
              { n: result.errors },
            )
          : t('settingsPages.migration.openItems.noErrors')}{' '}
        {done ? '' : t('settingsPages.migration.openItems.tieAfter')}
      </div>
      <div className="flex items-center gap-4">
        <Checkbox
          label={t('settingsPages.migration.openItems.onlyErrors')}
          checked={onlyErrors}
          onChange={(e) => setOnlyErrors(e.target.checked)}
        />
        {result.errors ? (
          <Button
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={() =>
              downloadCsv(`${side}-open-items-errors.csv`, [
                [
                  t('settingsPages.migration.openItems.col.row'),
                  t('settingsPages.migration.openItems.col.number'),
                  t('settingsPages.migration.openItems.col.type'),
                  who,
                  t('settingsPages.migration.openItems.col.date'),
                  t('settingsPages.migration.openItems.col.openBalance'),
                  t('settingsPages.migration.openItems.col.errors'),
                ],
                ...result.rows
                  .filter((r) => r.status === 'error')
                  .map((r) => [
                    String(r.row),
                    r.number,
                    r.type,
                    r.name,
                    formatDate(r.date),
                    r.open,
                    r.errors.join('; '),
                  ]),
              ])
            }
          >
            <Download className="size-4" /> {t('settingsPages.migration.openItems.downloadErrors')}
          </Button>
        ) : null}
      </div>
      <div className="max-h-[520px] overflow-y-auto rounded-lg border bg-card">
        <Table>
          <TableHeader className="sticky top-0 bg-card">
            <TableRow>
              <TableHead className="w-[60px] text-end">
                {t('settingsPages.migration.openItems.col.row')}
              </TableHead>
              <TableHead className="w-[110px]">
                {t('settingsPages.migration.openItems.col.date')}
              </TableHead>
              <TableHead className="w-[120px]">
                {t('settingsPages.migration.openItems.col.type')}
              </TableHead>
              <TableHead className="w-[100px]">
                {t('settingsPages.migration.openItems.col.number')}
              </TableHead>
              <TableHead>{who}</TableHead>
              <TableHead className="w-[110px]">
                {t('settingsPages.migration.openItems.col.dueDate')}
              </TableHead>
              <TableHead className="w-[120px] text-end">
                {t('settingsPages.migration.openItems.col.amount')}
              </TableHead>
              <TableHead className="w-[130px] text-end">
                {t('settingsPages.migration.openItems.col.openBalance')}
              </TableHead>
              <TableHead className="w-[100px]">
                {t('settingsPages.migration.openItems.col.status')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.row} className={r.status === 'error' ? 'align-top' : undefined}>
                <TableCell className="tnum text-end text-muted-foreground">{r.row}</TableCell>
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.number}</TableCell>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  {r.errors.map((e) => (
                    <div key={e} className="text-sm text-destructive">
                      {e}
                    </div>
                  ))}
                </TableCell>
                <TableCell>{r.dueDate ? formatDate(r.dueDate) : ''}</TableCell>
                <TableCell className="tnum text-end">{fmtAmount(r.amount)}</TableCell>
                <TableCell className="tnum text-end">
                  {r.kind === 'credit' ? '-' : ''}
                  {fmtAmount(r.open)}
                </TableCell>
                <TableCell>
                  {r.status === 'error' ? (
                    <StatusChip tone="danger">
                      {t('settingsPages.migration.openItems.status.error')}
                    </StatusChip>
                  ) : r.status === 'imported' ? (
                    <StatusChip tone="success">
                      {t('settingsPages.migration.openItems.status.imported')}
                    </StatusChip>
                  ) : (
                    <StatusChip tone="info">
                      {t('settingsPages.migration.openItems.status.ready')}
                    </StatusChip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        {done ? (
          <>
            <Button variant="outline" onClick={reset}>
              {t('settingsPages.migration.openItems.importAnother')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={side === 'receivable' ? '/sales/invoices' : '/expenses/bills'}>
                  {t(`settingsPages.migration.openItems.${side}.view`)}
                </Link>
              </Button>
              <Button asChild>
                <Link to="/settings/migration">{t('settingsPages.migration.backToChecklist')}</Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={reset}>
              {t('settingsPages.migration.back')}
            </Button>
            <Button disabled={run.isPending || result.ok === 0} onClick={() => run.mutate()}>
              {run.isPending
                ? t('settingsPages.migration.openItems.importing')
                : t('settingsPages.migration.openItems.importItems', { count: result.ok })}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
